"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { User, Settings, LogOut, ChevronDown, Landmark, Bell } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";
import { SportsbookPreferencesDialog } from "@/components/sportsbook-preferences/lazy-sportsbook-preferences-dialog";

interface UserMenuProps {
  collapsed?: boolean;
  variant?: "desktop" | "mobile";
  onNavigate?: () => void;
}

export function UserMenu({ collapsed = false, variant = "desktop", onNavigate }: UserMenuProps) {
  const isMobile = variant === "mobile";
  const pathname = usePathname();
  const { user, signOut, getProfileData, isLoading } = useAuth();
  const [open, setOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [initials, setInitials] = useState<string>("");
  const [displayName, setDisplayName] = useState<string>("");
  const [sportsbookManagerOpen, setSportsbookManagerOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      setInitials("");
      setDisplayName("");
      return;
    }

    const emailInitial = user.email?.[0]?.toUpperCase() || "U";
    setInitials(emailInitial);
    setDisplayName(user.email || "");

    getProfileData().then(({ userData }) => {
      if (userData?.first_name || userData?.last_name) {
        const firstInitial = userData.first_name?.[0]?.toUpperCase() || "";
        const lastInitial = userData.last_name?.[0]?.toUpperCase() || "";
        setInitials(firstInitial + lastInitial || emailInitial);

        const fullName = [userData.first_name, userData.last_name]
          .filter(Boolean)
          .join(" ");
        setDisplayName(fullName || user.email || "");
      }
    });
  }, [user, getProfileData]);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    setOpen(false);
    await signOut();
    setIsSigningOut(false);
  };

  const isAccountPage = pathname === "/account";
  const isNotificationsPage = pathname === "/account/notifications";

  if (!user && !isLoading) {
    return null;
  }

  if (isLoading) {
    return (
      <div
        className={cn(
          "h-8 w-8 rounded-full animate-pulse",
          "bg-gradient-to-br from-white/10 via-white/5 to-white/10",
          "ring-1 ring-white/10"
        )}
      />
    );
  }

  const avatarButton = (
    <button
      type="button"
      className={cn(
        "group relative flex items-center gap-2 transition-all duration-200",
        "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/40",
        "hover:bg-white/5",
        isMobile
          ? "min-h-11 min-w-11 flex-1 gap-3 rounded-lg px-1.5 text-left"
          : cn("rounded-full active:scale-[0.98]", collapsed ? "p-0" : "pl-0.5 pr-2 py-0.5")
      )}
      aria-label="User menu"
    >
      <div
        className={cn(
          "flex items-center justify-center rounded-full",
          "bg-primary/15 ring-1 ring-primary/25",
          "group-hover:bg-primary/20 group-hover:ring-primary/40",
          "transition-all duration-200",
          "h-8 w-8"
        )}
      >
        <span className="text-xs font-semibold text-white/90 select-none">
          {initials || <User className="size-4 text-white/70" />}
        </span>
      </div>

      {isMobile && (
        <span className="min-w-0 flex-1 truncate text-[14px] font-medium text-zinc-300" data-ph-mask>
          {displayName}
        </span>
      )}

      {!collapsed && (
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-white/40 transition-transform duration-200",
            open && "rotate-180"
          )}
        />
      )}
    </button>
  );

  const menuContent = (
    <div className="w-64 p-1">
      <div className="p-3 border-b border-white/5 mb-1" data-ph-mask>
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "flex items-center justify-center rounded-full",
              "bg-primary/15 ring-1 ring-primary/25",
              "h-10 w-10"
            )}
          >
            <span className="text-sm font-semibold text-white/90 select-none">
              {initials || <User className="size-5 text-white/70" />}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white/90 truncate">
              {displayName}
            </p>
            {user && displayName !== user.email && user.email && (
              <p className="text-xs text-white/50 truncate">
                {user.email}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-0.5">
        <Link
          href="/account"
          onClick={() => {
            setOpen(false);
            onNavigate?.();
          }}
          className={cn(
            "group flex items-center gap-3 px-3 py-2.5 rounded-md",
            "transition-all duration-150",
            isAccountPage
              ? "bg-white/10 text-white"
              : "text-white/70 hover:bg-white/5 hover:text-white"
          )}
        >
          <Settings className="size-4 opacity-70 group-hover:opacity-100 transition-opacity" />
          <span className="text-sm font-medium">Account Settings</span>
        </Link>

        <Link
          href="/account/notifications"
          onClick={() => {
            setOpen(false);
            onNavigate?.();
          }}
          className={cn(
            "group flex items-center gap-3 px-3 py-2.5 rounded-md",
            "transition-all duration-150",
            isNotificationsPage
              ? "bg-white/10 text-white"
              : "text-white/70 hover:bg-white/5 hover:text-white"
          )}
        >
          <Bell className="size-4 opacity-70 group-hover:opacity-100 transition-opacity" />
          <span className="text-sm font-medium">Notifications</span>
        </Link>

        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setSportsbookManagerOpen(true);
          }}
          className={cn(
            "group flex w-full items-center gap-3 rounded-md px-3 py-2.5",
            "text-white/70 transition-all duration-150 hover:bg-white/5 hover:text-white",
          )}
        >
          <Landmark className="size-4 opacity-70 transition-opacity group-hover:opacity-100" />
          <span className="text-sm font-medium">Manage Books</span>
        </button>

        <button
          type="button"
          onClick={handleSignOut}
          disabled={isSigningOut}
          className={cn(
            "group flex items-center gap-3 px-3 py-2.5 rounded-md w-full",
            "transition-all duration-150",
            "text-white/70 hover:bg-destructive/10 hover:text-destructive",
            isSigningOut && "opacity-50 cursor-not-allowed"
          )}
        >
          <LogOut className="size-4 opacity-70 group-hover:opacity-100 transition-opacity" />
          <span className="text-sm font-medium">
            {isSigningOut ? "Signing out…" : "Sign Out"}
          </span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>{avatarButton}</PopoverTrigger>
        <PopoverContent
          side={isMobile ? "top" : "right"}
          align={isMobile ? "start" : "end"}
          sideOffset={8}
          className={cn(
            "p-0 border-white/10 bg-surface-overlay backdrop-blur-xl",
            "shadow-xl shadow-black/20",
            "animate-in fade-in-0 zoom-in-95",
            isMobile ? "slide-in-from-bottom-2" : "slide-in-from-left-2",
            "duration-200"
          )}
        >
          {menuContent}
        </PopoverContent>
      </Popover>
      <SportsbookPreferencesDialog
        open={sportsbookManagerOpen}
        onOpenChange={setSportsbookManagerOpen}
        source="user_menu"
      />
    </>
  );
}
